/* eslint-disable react/no-array-index-key */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import useTheme from '@mui/styles/useTheme';
import Paper from '@mui/material/Paper';
import { Backdrop } from '@mui/material';
import { CircularProgress } from '@mui/material';
import isEmpty from 'lodash/isEmpty';
import { event } from '../../utils/analytics';
import CustomerAccount from './CustomerAccounts';
import { useUserApi } from '../../apis';
import useAuth from '../../authentication/useAuth';
import useIsMountedRef from '../../hooks/useIsMountedRef';

const useStyle = makeStyles(() => ({
	visuallyHidden: {
		border: 0,
		clip: 'rect(0 0 0 0)',
		height: 1,
		margin: -1,
		overflow: 'hidden',
		padding: 0,
		position: 'absolute',
		top: 20,
		width: 1,
	},
}));

function descendingComparator(a, b, orderBy) {
	if (b[orderBy] < a[orderBy] || !b[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy] || !a[orderBy]) {
		return 1;
	}
	return 0;
}

function getComparator(order, orderBy) {
	return order === 'desc'
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
	const { t } = useTranslation('common');
	const { classes, order, orderBy, onRequestSort } = props;
	const createSortHandler = (property) => (ev) => {
		onRequestSort(ev, property);
	};

	return (
		<TableHead>
			<TableRow>
				<TableCell>{t('impersonation')}</TableCell>
				<TableCell
					key="companyName"
					sortDirection={orderBy === 'companyName' ? order : false}
				>
					<TableSortLabel
						active={orderBy === 'companyName'}
						direction={orderBy === 'companyName' ? order : 'asc'}
						onClick={createSortHandler('companyName')}
					>
						{t('company_name')}
						{orderBy === 'companyName' ? (
							<span className={classes.visuallyHidden}>
								{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
							</span>
						) : null}
					</TableSortLabel>
				</TableCell>
				<TableCell
					key="fullName"
					sortDirection={orderBy === 'fullName' ? order : false}
				>
					<TableSortLabel
						active={orderBy === 'fullName'}
						direction={orderBy === 'fullName' ? order : 'asc'}
						onClick={createSortHandler('fullName')}
					>
						{t('full_name')}
						{orderBy === 'fullName' ? (
							<span className={classes.visuallyHidden}>
								{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
							</span>
						) : null}
					</TableSortLabel>
				</TableCell>
				<TableCell>{t('access')}</TableCell>
				<TableCell>{t('email')}</TableCell>
			</TableRow>
		</TableHead>
	);
}

EnhancedTableHead.propTypes = {
	onRequestSort: PropTypes.func.isRequired,
	order: PropTypes.oneOf(['asc', 'desc']).isRequired,
	orderBy: PropTypes.string.isRequired,
	classes: PropTypes.object.isRequired,
};

function stableSort(array, comparator) {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

function Impersonate({ namespace }) {
	const classes = useStyle();
	const theme = useTheme();
	const { t } = useTranslation(namespace, 'common');
	const navigate = useNavigate();
	const isMountedRef = useIsMountedRef();
	const { getAccounts, getAccountDetails } = useUserApi();

	const {
		impersonate,
		endImpersonation,
		isImpersonating,
		authenticatedUser,
		getCurrentUser,
	} = useAuth();

	const [customers, setCustomers] = useState(null);
	const [order, setOrder] = React.useState('asc');
	const [orderBy, setOrderBy] = React.useState('companyName');
	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const [selectedCustomerEmail, setSelectedCustomerEmail] = useState('');
	const [isCustomerAccountOpen, setIsCustomerAccountOpen] = useState(false);
	const [customerDetailsByEmail, setCustomerDetailsByEmail] = useState({});
	const [isCustomerAccountLoading, setIsCustomerAccountLoading] =
		useState(false);
	const detailsRequestIdRef = useRef(0);

	const startImpersonate = async (email) => {
		event('impersonate', email);
		await impersonate(email);
		navigate('/');
	};

	const handleRequestSort = (ev, property) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const endImpersonate = async () => {
		await endImpersonation();
	};

	const fetchCustomerAccountDetails = useCallback(
		async (email) => {
			if (!email || !authenticatedUser.canImpersonate) {
				return;
			}

			if (customerDetailsByEmail[email]) {
				if (isMountedRef.current) {
					setIsCustomerAccountLoading(false);
				}
				return;
			}

			const requestId = detailsRequestIdRef.current + 1;
			detailsRequestIdRef.current = requestId;

			if (isMountedRef.current) {
				setIsCustomerAccountLoading(true);
			}

			try {
				const accountDetailsResponse = await getAccountDetails(
					authenticatedUser.email,
					email
				);
				const isApiAborted = Boolean(accountDetailsResponse?.isAborted);
				const isApiError = Boolean(accountDetailsResponse?.isError);
				const responseData = accountDetailsResponse?.data;

				if (isApiAborted) {
					return;
				}

				if (isApiError) {
					console.error(accountDetailsResponse?.error);
					return;
				}

				if (!isEmpty(responseData) && isMountedRef.current) {
					setCustomerDetailsByEmail((previousDetails) => ({
						...previousDetails,
						[email]: responseData,
					}));
				}
			} finally {
				if (isMountedRef.current && detailsRequestIdRef.current === requestId) {
					setIsCustomerAccountLoading(false);
				}
			}
		},
		[
			authenticatedUser.canImpersonate,
			authenticatedUser.email,
			customerDetailsByEmail,
			getAccountDetails,
			isMountedRef,
		]
	);

	const openCustomerAccount = (email) => {
		setSelectedCustomerEmail(email);
		setIsCustomerAccountOpen(true);
		fetchCustomerAccountDetails(email);
	};

	const closeCustomerAccount = () => {
		setIsCustomerAccountOpen(false);
	};

	const selectedCustomerAccountDetail =
		customerDetailsByEmail[selectedCustomerEmail] || {};

	// -------------------------------------------------------------------------------
	// Fetch the list of customers (accounts) that the current user can impersonate
	// -------------------------------------------------------------------------------
	const fetchCustomers = useCallback(async () => {
		if (!authenticatedUser.canImpersonate) {
			return;
		}

		const setLoadingSafe = (value) => {
			if (isMountedRef.current) {
				setIsLoading(value);
			}
		};

		const setErrorSafe = (value) => {
			if (isMountedRef.current) {
				setIsError(value);
			}
		};

		const setCustomersSafe = (value) => {
			if (isMountedRef.current) {
				setCustomers(value);
			}
		};

		setLoadingSafe(true);
		setErrorSafe(false);

		try {
			const accountsResponse = await getAccounts(authenticatedUser.email);
			const isApiAborted = Boolean(accountsResponse?.isAborted);
			const isApiError = Boolean(accountsResponse?.isError);
			const responseData = accountsResponse?.data;

			if (isApiAborted) {
				return;
			}

			if (isApiError) {
				console.error(accountsResponse?.error);
				setErrorSafe(true);
				return;
			}

			if (!isEmpty(responseData)) {
				setCustomersSafe(responseData);
			}
		} finally {
			setLoadingSafe(false);
		}
	}, [
		isMountedRef,
		authenticatedUser.canImpersonate,
		authenticatedUser.email,
		getAccounts,
	]);

	useEffect(() => {
		fetchCustomers();
	}, [isImpersonating, fetchCustomers]);

	const getImpersonationButton = (email) => {
		if (!email) {
			return undefined;
		}
		if (isImpersonating && getCurrentUser().email === email) {
			return (
				<Button
					variant="contained"
					color="primary"
					size="large"
					fullWidth
					role="button"
					data-testid={t('end')}
					aria-label={t('end')}
					onClick={() => endImpersonate()}
				>
					{t('end')}
				</Button>
			);
		}
		return (
			<Button
				variant="contained"
				color="primary"
				size="large"
				fullWidth
				role="button"
				data-testid={t('start')}
				aria-label={t('start')}
				onClick={() => startImpersonate(email)}
			>
				{t('start')}
			</Button>
		);
	};

	return (
		<Container maxWidth="xl">
			<Backdrop
				sx={{
					color: 'primary',
					zIndex: theme.zIndex.drawer + 1,
					backgroundColor: 'transparent',
				}}
				open={isLoading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			{!isLoading && (
				<Grid
					container
					spacing={0}
					style={{
						marginTop: theme.spacing(4),
						marginBottom: theme.spacing(4),
					}}
				>
					<Grid
						container
						spacing={3}
						direction="column"
						alignItems="center"
						justifyContent="center"
						style={{
							marginTop: theme.spacing(-5),
							marginBottom: theme.spacing(6),
						}}
					>
						<Grid item sx={{ textAlign: 'center' }}>
							<Typography variant="heading1">{t('title')}</Typography>
							<Box display="flex" justifyContent="center" alignItems="center">
								<Grid item sm={6} sx={{ marginTop: theme.spacing(3) }}>
									<Typography
										variant="heading4"
										color="textSecondary"
										style={{ textAlign: 'center' }}
									>
										{t('subtitle')}
									</Typography>
								</Grid>
							</Box>
						</Grid>
					</Grid>

					<Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none' }}>
						<TableContainer>
							<Table aria-label="sticky table">
								<EnhancedTableHead
									classes={classes}
									order={order}
									orderBy={orderBy}
									onRequestSort={handleRequestSort}
								/>
								<TableBody>
									{customers &&
										stableSort(customers, getComparator(order, orderBy)).map(
											(customer) => (
												<TableRow key={customer.email}>
													<TableCell>
														{getImpersonationButton(customer.email)}
													</TableCell>
													<TableCell className="impersonate-companyName">
														{customer.companyName}
													</TableCell>
													<TableCell className="impersonate-fullName">
														{customer.fullName}
													</TableCell>
													<TableCell align="left" className="impersonate-Email">
														<Button
															variant="text"
															color="primary"
															size="large"
															fullWidth
															role="button"
															data-testid={t('view_accounts')}
															aria-label={t('view_accounts')}
															onClick={() =>
																openCustomerAccount(customer.email)
															}
														>
															{t('view_accounts')}
														</Button>
													</TableCell>
													<TableCell className="impersonate-Email">
														{customer.email}
													</TableCell>
												</TableRow>
											)
										)}
								</TableBody>
							</Table>
						</TableContainer>
					</Paper>
				</Grid>
			)}
			<CustomerAccount
				customerEmail={selectedCustomerEmail}
				isOpen={isCustomerAccountOpen}
				onClose={closeCustomerAccount}
				accountDetail={selectedCustomerAccountDetail}
				isLoading={
					isCustomerAccountLoading && isEmpty(selectedCustomerAccountDetail)
				}
			/>
			<Backdrop
				sx={{
					color: 'primary',
					zIndex: theme.zIndex.drawer + 1,
					backgroundColor: 'transparent',
				}}
				open={isError || (!isError && !customers && !isLoading)}
			>
				{t('no_customers_found')}
			</Backdrop>
		</Container>
	);
}

Impersonate.propTypes = {
	namespace: PropTypes.string,
};

export default Impersonate;
